import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { createOrganization } from "../../store/actions/organizationAction";
import {
  isHaveHTMLElement,
  sanitizeInput,
} from "../../helpers/inputProcessing";
import { TypographySmall } from "../customComponent";
import { colorStyling } from "../../helpers/color";
import dayjs from "dayjs";

const CreateOrganization = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("organization");
  const { t: tCommon } = useTranslation();

  const [organizationName, setOrganizationName] = useState("");
  const [nameValidation, setNameValidation] = useState("");
  const [viewerNumber, setViewerNumber] = useState(100);
  const [subscriptionDate, setSubscriptionDate] = useState(null);
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
    setOrganizationName("");
    setNameValidation("");
    setViewerNumber(100);
    setSubscriptionDate(null);
  };

  const handleOrganizationName = (e) => {
    setOrganizationName(e.target.value);
    setNameValidation("");
  };

  const handleChangeViewerNumber = (number) => {
    let sanitizedNumber = number.toString().replace(/^0+/, "") || "0";

    if (sanitizedNumber === "") sanitizedNumber = "0";

    const min = 1;
    const max = 1000;
    sanitizedNumber = +sanitizedNumber;
    if (sanitizedNumber < min) sanitizedNumber = min;
    if (sanitizedNumber > max) sanitizedNumber = max;

    setViewerNumber(sanitizedNumber);
  };

  const handleChangeCheckbox = (event) => {
    if (event.target.checked) {
      setSubscriptionDate(dayjs(new Date()).format("YYYY-MM-DD"));
    } else {
      setSubscriptionDate(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (organizationName.trim() === "") {
      setNameValidation(tCommon("validation.fieldMustNotEmpty"));
      setOrganizationName("");
      return;
    }
    if (isHaveHTMLElement(organizationName)) {
      setNameValidation(tCommon("validation.noHTMLAllowed"));
      return;
    }

    const payload = { name: organizationName.trim() };
    payload["name"] = sanitizeInput(payload.name);
    payload["screensQuota"] = +viewerNumber;

    let subsDatePayload = subscriptionDate
      ? new Date(subscriptionDate + "T23:59:59").toISOString()
      : null;
    payload["subscriptionEndAt"] = subsDatePayload;
    dispatch(createOrganization(payload));
    handleClickClose();
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleClickOpen}
        startIcon={<AddIcon />}
      >
        {t("createBtn")}
      </Button>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        keepMounted
        onClose={handleClickClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {t("createDialogTitle")}
            <IconButton onClick={handleClickClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <InputLabel>{t("form.nameLabel")}</InputLabel>
              <TextField
                fullWidth
                margin="dense"
                name="groupName"
                value={organizationName}
                onChange={handleOrganizationName}
                placeholder={t("form.namePlaceholder")}
                required
              />
              <TypographySmall sx={{ color: colorStyling.red }}>
                {nameValidation}
              </TypographySmall>
            </Box>

            <Box sx={{ mb: 2 }}>
              <InputLabel>{t("form.viewerLimitLabel")}</InputLabel>
              <TextField
                fullWidth
                margin="dense"
                type="number"
                value={viewerNumber}
                onChange={(e) => {
                  handleChangeViewerNumber(e.target.value);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                inputProps={{
                  min: 1,
                  max: 1000,
                }}
              />
            </Box>
            <Box>
              <InputLabel>
                {t("form.subscriptionLabel")}
                <Checkbox
                  value={subscriptionDate}
                  onChange={handleChangeCheckbox}
                />
              </InputLabel>
              {subscriptionDate && (
                <TextField
                  fullWidth
                  margin="dense"
                  type="date"
                  value={subscriptionDate}
                  onChange={(e) => {
                    setSubscriptionDate(
                      dayjs(new Date(e.target.value)).format("YYYY-MM-DD")
                    );
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClickClose} variant="outlined" fullWidth>
              {t("form.cancelBtn")}
            </Button>
            <Button type="submit" variant="contained" fullWidth>
              {t("form.createBtn")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default CreateOrganization;
